import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../common/button/commonButton";
import ReusableTable from "../../common/table/CommonTable";
import ReusableFormFields from "../../common/textfield/ReusableTextfield";
import { fetchBlocks } from "../../redux/block/blockAction";
import {
  addFloor,
  deleteFloor,
  fetchallFloors,
  fetchAllFloors,
  resetCurrentFloor,
  updateFloor,
} from "../../redux/floor/floorAction";

const FloorList = () => {
  const [floors, setFloors] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState(null);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [blockOptions, setBlockOptions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);

  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    reset,
    control,
  } = useForm({
    defaultValues: { floor_name: "", block: "", status: true },
  });

  const watchStatus = watch("status", true);
  const floorList = useSelector((state) => state?.floor?.fetchAllFloor || {});
  const blockList = useSelector((state) => state?.block?.fetchBlocks || {});
  const floorAdd = useSelector((state) => state?.floor?.addFloor || {});
  const floorUpdate = useSelector((state) => state?.floor?.updateFloor || {});
  const floorDelete = useSelector((state) => state?.floor?.deleteFloor || {});
  const totalPages = floorList?.floors?.totalPages || 1;

  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  const fetchData = useCallback(
    (page) => {
      dispatch(fetchBlocks());
      dispatch(fetchallFloors(page));
    },
    [dispatch]
  );

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  useEffect(() => {
    if (floorDelete?.success) {
      setSnackbarMessage(`Floor has been deleted successfully`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleCloseDeleteDialog();
      dispatch(fetchAllFloors(currentPage));
    } else if (floorDelete?.error) {
      setSnackbarMessage(floorDelete.errorMessage || "Failed to delete floor");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [floorDelete, dispatch, currentPage]);

  useEffect(() => {
    if (floorList?.floors?.data) {
      setFloors(
        floorList.floors.data.map((floor, index) => ({
          id: floor.id,
          serial_no: index + 1,
          block: floor.Block.block_name,
          floor: floor.floor_name,
          block_status: floor.floor_status,
          status: floor.floor_status === 1,
          originalData: floor,
        }))
      );
    }
  }, [floorList]);

  useEffect(() => {
    if (blockList?.Blocks?.data) {
      setBlockOptions(
        blockList.Blocks.data
          .filter((block) => block.block_status !== 2)
          .map((block) => ({
            value: block.id,
            label: block.block_name,
          }))
      );
    }
  }, [blockList]);

  useEffect(() => {
    if (floorAdd.success) {
      setSnackbarMessage("Floor added successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleCloseDialog();
      dispatch(resetCurrentFloor());
      dispatch(fetchAllFloors(currentPage));
    } else if (floorAdd.error) {
      setErrorMessage(floorAdd.errorMessage || "Failed to add floor");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [floorAdd, dispatch, currentPage]);

  useEffect(() => {
    if (floorUpdate.success) {
      setSnackbarMessage("Floor updated successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleCloseDialog();
      dispatch(resetCurrentFloor());
      dispatch(fetchAllFloors(currentPage));
    } else if (floorUpdate.error) {
      setErrorMessage(floorUpdate.errorMessage || "Failed to update floor");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [floorUpdate, dispatch, currentPage]);

  const columns = [
    { id: "serial_no", label: "S.No", sortable: true },
    { id: "block", label: "Block", sortable: true },
    { id: "floor", label: "Floor", sortable: true },
    {
      id: "block_status",
      label: "Status",
      sortable: true,
      render: (value) => (
        <Chip
          label={value === 1 ? "Active" : "Inactive"}
          color={value === 1 ? "success" : "error"}
          size="small"
        />
      ),
    },
  ];

  const handleOpenDialog = (mode, floor = null) => {
    setDialogMode(mode);
    setSelectedFloor(floor);
    setErrorMessage("");
    reset();

    if (mode === "edit" && floor) {
      setValue("floor_name", floor.floor);
      setValue("block", floor.originalData.block_id);
      setValue("status", floor.status);
    } else {
      setValue("floor_name", "");
      setValue("block", blockOptions.length > 0 ? blockOptions[0].value : "");
      setValue("status", true);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setErrorMessage("");
    reset();
  };

  const handleOpenDeleteDialog = (floor) => {
    setFloorToDelete(floor);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setFloorToDelete(null);
  };

  const handleDeleteFloor = () => {
    if (floorToDelete) {
      dispatch(deleteFloor(floorToDelete.id));
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const onSubmit = (data) => {
    setErrorMessage("");

    if (!data.floor_name || data.floor_name.trim() === "") {
      setErrorMessage("Floor name is required");
      return;
    }

    if (!data.block) {
      setErrorMessage("Please select a block");
      return;
    }

    const floorData = {
      floor_name: data.floor_name.trim(),
      block_id: data.block,
      floor_status: data.status ? 1 : 0,
    };

    if (dialogMode === "add") {
      dispatch(addFloor(floorData));
    } else {
      dispatch(updateFloor(selectedFloor.id, floorData));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Floor Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          sx={{
            backgroundColor: "#2c90f7",
            color: "white",
            borderRadius: "20px",
            px: 3,
            py: 1,
            textTransform: "none",
            fontWeight: "bold",
          }}
        >
          Add New Floor
        </Button>
      </Box>

      <ReusableTable
        columns={columns}
        data={floors}
        isLoading={floorList.inProgress}
        title="Floors"
        onEditClick={(floor) => handleOpenDialog("edit", floor)}
        onDeleteClick={(floor) => handleOpenDeleteDialog(floor)}
        customEmptyMessage="No floors found. Add a new floor to get started."
        headerColor="#000000"
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        withActions={true}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#129bab",
            color: "white",
          }}
        >
          {dialogMode === "add" ? "Add New Floor" : "Edit Floor"}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}
            <Paper elevation={0} sx={{ p: 2 }}>
              <ReusableFormFields
                inputFields={[
                  {
                    name: "block",
                    label: "Block",
                    type: "select",
                    options: blockOptions,
                    required: true,
                    rules: { required: "Block is required" },
                  },
                  {
                    name: "floor_name",
                    label: "Floor Name",
                    type: "text",
                    required: true,
                    rules: { required: "Floor name is required" },
                  },
                ]}
                register={register}
                errors={errors}
                control={control}
                setValue={setValue}
              />

              {dialogMode === "edit" && (
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={watchStatus}
                        onChange={(e) => setValue("status", e.target.checked)}
                        sx={{
                          "& .MuiSwitch-thumb": {
                            backgroundColor: watchStatus
                              ? "#129bab"
                              : "#f44336",
                          },
                          "& .MuiSwitch-track": {
                            backgroundColor: watchStatus
                              ? "#129bab"
                              : "#f44336",
                          },
                        }}
                      />
                    }
                    label="Active"
                  />
                </Box>
              )}
            </Paper>
          </DialogContent>

          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: "#129bab",
              justifyContent: "center",
            }}
          >
            <CustomButton
              onClick={handleCloseDialog}
              text="Cancel"
              variant="contained"
              disableElevation
              size="small"
            />
            <CustomButton
              type="submit"
              text={dialogMode === "add" ? "Add" : "Update"}
              startIcon={<AddIcon />}
              disabled={floorAdd?.inProgress || floorUpdate?.inProgress}
              loading={floorAdd?.inProgress || floorUpdate?.inProgress}
              variant="contained"
              disableElevation
              size="small"
            />
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            bgcolor: "#f44336",
            color: "white",
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Are you sure you want to delete floor "{floorToDelete?.floor}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            justifyContent: "center",
          }}
        >
          <CustomButton
            onClick={handleCloseDeleteDialog}
            text="Cancel"
            variant="outlined"
            disableElevation
            size="small"
          />
          <CustomButton
            onClick={handleDeleteFloor}
            text="Delete"
            disabled={floorDelete?.inProgress}
            loading={floorDelete?.inProgress}
            color="error"
            variant="contained"
            disableElevation
            size="small"
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorList;
